import React from 'react';

import reportTypes, { ReportTypes, SelectableReportTypes } from '../../../constants/reportTypes';

import AccountFirstInitiator from '../reportTypesForm/create/AccountFirstInitiator';
import BusinessUsageReport from '../reportTypesForm/create/BusinessUsageReport';
import CaseOverview, { caseOverviewTransform } from '../reportTypesForm/create/CaseOverview';
import CaseStatusHistory from '../reportTypesForm/create/CaseStatusHistory';
import EntitiesOverview from '../reportTypesForm/create/EntitiesOverview';
import GroupDuration from '../reportTypesForm/create/GroupDuration';
import KriReport from '../reportTypesForm/create/KriReport';
import PepSanctionHits from '../reportTypesForm/create/PepSanctionHits';
import PingOverview from '../reportTypesForm/create/PingOverview';
import PortfolioScreening, { portfolioScreeningTransform } from '../reportTypesForm/create/PortfolioScreening';
import RequestOverview from '../reportTypesForm/create/RequestOverview';
import RuleOverview from '../reportTypesForm/create/RuleOverview';
import RuleStatistics from '../reportTypesForm/create/RuleStatistics';
import EntityTransactions from '../reportTypesForm/create/EntityTransactions';
import ExtendedTransactions from '../reportTypesForm/create/ExtendedTransactions';
import RuleThresholdVariation from '../reportTypesForm/create/RuleThresholdVariation';

const reportCreateMap = {
  [reportTypes.ACCOUNT_FIRST_INITIATOR]: AccountFirstInitiator,
  [reportTypes.BUSINESS_USAGE_REPORT]: BusinessUsageReport,
  [reportTypes.CASE_OVERVIEW]: CaseOverview,
  [reportTypes.CASE_STATUS_HISTORY_REPORT]: CaseStatusHistory,
  [reportTypes.ENTITY_OVERVIEW]: EntitiesOverview,
  [reportTypes.ENTITY_TRANSACTIONS]: EntityTransactions,
  [reportTypes.EXTENDED_TRANSACTIONS]: ExtendedTransactions,
  [reportTypes.GROUP_DURATION]: GroupDuration,
  [reportTypes.KRI_REPORT]: KriReport,
  [reportTypes.PEP_SANCTION_HITS_THRESHOLD_VARIATION]: PepSanctionHits,
  [reportTypes.PING_OVERVIEW]: PingOverview,
  [reportTypes.PORTFOLIO_SCREENING_REPORT]: PortfolioScreening,
  [reportTypes.REQUEST_OVERVIEW]: RequestOverview,
  [reportTypes.RULE_OVERVIEW]: RuleOverview,
  [reportTypes.RULE_STATISTICS]: RuleStatistics,
  [reportTypes.RULE_THRESHOLD_VARIATION]: RuleThresholdVariation,
} satisfies Record<SelectableReportTypes, React.FC | null>;

export const reportDescription = {
  [reportTypes.ACCOUNT_FIRST_INITIATOR]: 'Summarises transacting-account counts for the selected period, with optional country-level and per-account indicator outputs.',
  [reportTypes.BUSINESS_USAGE_REPORT]: 'Analyses individual customer interactions with selected businesses, including transaction volumes, group memberships, and form submission history.',
  [reportTypes.CASE_OVERVIEW]: 'Exports all AML investigation cases with status, duration, entity links, triggers, and analyst assignment data.',
  [reportTypes.CASE_STATUS_HISTORY_REPORT]: 'Exports case status changes with update timestamps, the members who made them, and current case details.',
  [reportTypes.ENTITY_OVERVIEW]: 'Snapshot of all registered entities with risk scores, risk classes, group memberships, and key identifiers.',
  [reportTypes.ENTITY_TRANSACTIONS]: 'Per-entity transaction volumes, frequencies, and daily statistics for incoming, outgoing, and processing transactions.',
  [reportTypes.EXTENDED_TRANSACTIONS]: 'Detailed transaction-level export with transaction context, ping metadata, and assignee details for the selected timeframe.',
  [reportTypes.GROUP_DURATION]: 'Measures days between two group membership events (assignment or unassignment) for each entity.',
  [reportTypes.KRI_REPORT]: 'Key Risk Indicator report with period-based AML/KYC operational metrics for requests, pings, cases, entities, sanctions, and filings.',
  [reportTypes.PEP_SANCTION_HITS_THRESHOLD_VARIATION]: 'Shows PEP, sanction, and adverse media match results at each threshold level (70–100) for individuals in the system.',
  [reportTypes.PORTFOLIO_SCREENING_REPORT]: 'Entity-level screening overview across PEP, sanction, and adverse media collections, including latest rule execution timestamps.',
  [reportTypes.PING_OVERVIEW]: 'Full list of AML alerts (pings) with rule, entity, status, and tag details, plus a per-rule statistics summary.',
  [reportTypes.REQUEST_OVERVIEW]: 'Detailed list of all requests processed by the platform with entity roles, amounts, currencies, and signal results.',
  [reportTypes.RULE_OVERVIEW]: 'Full parameter snapshot of all rule versions in the system — supports governance, audit, and rule change documentation.',
  [reportTypes.RULE_STATISTICS]: 'Per-rule performance metrics including hit rates, ping counts per status, and transaction coverage during the period.',
  [reportTypes.RULE_THRESHOLD_VARIATION]: 'Backtests how many pings each TM rule would have generated if thresholds were multiplied by selected factors.',
} satisfies Record<SelectableReportTypes, string | undefined>;

export const mapReportTransform: Record<ReportTypes, ((data: unknown) => unknown) | undefined > = {
  [reportTypes.ACCOUNT_FIRST_INITIATOR]: undefined,
  [reportTypes.BUSINESS_USAGE_REPORT]: undefined,
  [reportTypes.CASE_OVERVIEW]: caseOverviewTransform,
  [reportTypes.CASE_STATUS_HISTORY_REPORT]: undefined,
  [reportTypes.CASE_PDF]: undefined,
  [reportTypes.CUSTOM]: undefined,
  [reportTypes.ENTITY_OVERVIEW]: undefined,
  [reportTypes.ENTITY_REPORT]: undefined,
  [reportTypes.ENTITY_TRANSACTIONS]: undefined,
  [reportTypes.EXTENDED_TRANSACTIONS]: undefined,
  [reportTypes.FIPO]: undefined,
  [reportTypes.GROUP_DURATION]: undefined,
  [reportTypes.KRI_REPORT]: undefined,
  [reportTypes.PEP_SANCTION_HITS_THRESHOLD_VARIATION]: undefined,
  [reportTypes.PING_OVERVIEW]: undefined,
  [reportTypes.PORTFOLIO_SCREENING_REPORT]: portfolioScreeningTransform,
  [reportTypes.REQUEST_OVERVIEW]: undefined,
  [reportTypes.RULE_STATISTICS]: undefined,
  [reportTypes.RULE_OVERVIEW]: undefined,
  [reportTypes.RULE_THRESHOLD_VARIATION]: undefined,
};

export default reportCreateMap;
